/**
 * Iter-158.13.2.20f.4 — Service-operator production-access console.
 *
 * Deliberately minimal. Lists pending Production-creation requests
 * and lets a service operator Approve or Deny each one. Gated by the
 * JWT-authenticated `/service/status` probe on mount — a non-operator
 * user who lands here directly gets the same "not authorised"
 * message a hostile browser bookmark would get. No secret ever
 * reaches the browser: the backend gate reads `ADMIN_EMAIL` /
 * `PRODUCTION_ACCESS_OPERATOR_EMAILS` server-side and simply accepts
 * or rejects the caller's regular Bearer token.
 *
 * NOT a Production role. Production Owner/Admin do NOT gain access
 * here just because they own a Production — the gate is the seeded
 * service-admin email only.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowLeft, Loader2, ShieldAlert, Film, Tv, Tv2, Wand2, Palette, Scissors, Video, Users } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

// Iter-158.13.2.20f.7.1 — human labels for the structured
// `requested_production` payload so the Service Operator sees the
// same words the requester saw in the wizard, not the raw enums.
const PRODUCTION_TYPE_LABELS = {
    feature_film: { label: "Feature Film", Icon: Film },
    tv_single: { label: "Single TV Episode", Icon: Tv },
    tv_series: { label: "Episodic TV Series", Icon: Tv2 },
};
const DESTINATION_LABELS = {
    hair_makeup: { label: "Hair & Makeup", Icon: Wand2 },
    production: { label: "Production", Icon: Users },
    art: { label: "Art", Icon: Palette },
    costume: { label: "Costume", Icon: Scissors },
    vfx_ai: { label: "VFX & AI", Icon: Video },
};

export default function ServiceProductionAccessPage() {
    const [authState, setAuthState] = useState("checking"); // checking | ok | denied
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [statusFilter, setStatusFilter] = useState("pending");
    const [busyId, setBusyId] = useState(null);
    const [decisionNotes, setDecisionNotes] = useState({});

    // Gate probe. We rely on the server-side check; the FE probe just
    // lets us hide the page contents from a curious non-operator who
    // typed the URL by hand. Every real authorisation decision is
    // still enforced server-side per request.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/service/status");
                if (cancelled) return;
                setAuthState(data?.is_service_operator ? "ok" : "denied");
            } catch {
                if (!cancelled) setAuthState("denied");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get(
                `/service/production-access/requests?status=${encodeURIComponent(statusFilter)}`,
            );
            setRows(data?.requests || []);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        if (authState === "ok") load();
    }, [authState, load]);

    const decide = async (row, action) => {
        setBusyId(row.id);
        try {
            await api.post(
                `/service/production-access/${row.id}/${action}`,
                { notes: (decisionNotes[row.id] || "").trim() || null },
            );
            toast.success(
                action === "approve"
                    ? `Approved — ${row.email || "requester"} can create their Production.`
                    : `Denied — ${row.email || "requester"} cannot create.`,
            );
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    if (authState === "checking") {
        return (
            <div data-testid="service-page-loading" className="min-h-screen flex items-center justify-center">
                <Loader2 className="animate-spin text-neutral-500" size={20} />
            </div>
        );
    }

    if (authState === "denied") {
        return (
            <div
                data-testid="service-page-denied"
                className="min-h-screen flex items-center justify-center px-6"
            >
                <div className="max-w-sm text-center">
                    <ShieldAlert size={32} className="mx-auto text-neutral-500" />
                    <h1 className="mt-4 font-display text-xl font-bold text-white">
                        Service operator only
                    </h1>
                    <p className="mt-2 text-sm text-neutral-400">
                        This surface is restricted to the OnSetFlow service operator. Production Owners and Administrators do not have access here.
                    </p>
                    <Link
                        to="/projects"
                        className="tap mt-6 inline-flex items-center gap-2 text-xs text-neutral-300 hover:text-white"
                    >
                        <ArrowLeft size={14} /> Back to My Projects
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="service-production-access-page" className="min-h-screen bg-neutral-950 text-neutral-100 px-4 py-6 sm:px-8 sm:py-10">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                            Service operator
                        </div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">
                            Production access requests
                        </h1>
                        <p className="text-neutral-400 text-sm mt-2 max-w-prose">
                            Approve or deny requests to create new Productions on OnSetFlow. Existing invitations into approved Productions are unaffected.
                        </p>
                    </div>
                    <Link
                        to="/projects"
                        data-testid="service-back-link"
                        className="tap shrink-0 text-xs text-neutral-400 hover:text-white flex items-center gap-1.5"
                    >
                        <ArrowLeft size={14} /> My Projects
                    </Link>
                </div>

                <div className="mt-6 flex items-center gap-2">
                    {["pending", "approved", "denied", "all"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            data-testid={`service-filter-${s}`}
                            onClick={() => setStatusFilter(s)}
                            className={`tap px-3 h-8 rounded-full text-[11px] uppercase tracking-[0.2em] border ${
                                statusFilter === s
                                    ? "bg-white text-neutral-950 border-white"
                                    : "bg-transparent text-neutral-400 border-neutral-700 hover:text-white"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>

                {error ? (
                    <p data-testid="service-error" className="mt-4 text-sm text-red-400">
                        {error}
                    </p>
                ) : null}

                <div className="mt-6 space-y-3">
                    {loading ? (
                        <div className="text-sm text-neutral-500 flex items-center gap-2">
                            <Loader2 className="animate-spin" size={14} /> Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <p data-testid="service-empty" className="text-sm text-neutral-500">
                            No {statusFilter === "all" ? "" : statusFilter} requests.
                        </p>
                    ) : (
                        rows.map((r) => {
                            const rp = r.requested_production || null;
                            const ptype = rp ? PRODUCTION_TYPE_LABELS[rp.production_type] : null;
                            const dest = rp && rp.destination_key ? DESTINATION_LABELS[rp.destination_key] : null;
                            return (
                            <div
                                key={r.id}
                                data-testid={`service-request-${r.id}`}
                                className="rounded-xl border border-white/10 bg-card p-4"
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div
                                            data-testid={`service-request-${r.id}-requester-name`}
                                            className="text-sm font-semibold text-foreground"
                                        >
                                            {r.name || "Unnamed requester"}
                                        </div>
                                        <div
                                            data-testid={`service-request-${r.id}-requester-email`}
                                            className="text-[12px] text-muted-foreground"
                                        >
                                            {r.email}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            Requested{" "}
                                            <span
                                                data-testid={`service-request-${r.id}-requested-at`}
                                            >
                                                {r.requested_at?.slice(0, 16).replace("T", " ")}
                                            </span>
                                            {" · "}Status:{" "}
                                            <span
                                                data-testid={`service-request-${r.id}-status`}
                                                className={
                                                    r.status === "pending"
                                                        ? "text-amber-300"
                                                        : r.status === "approved"
                                                            ? "text-emerald-300"
                                                            : "text-red-300"
                                                }
                                            >
                                                {r.status}
                                            </span>
                                        </div>
                                        {/* Iter-158.13.2.20f.7.1 —
                                            requested Production summary
                                            so the operator has full
                                            context (name / type /
                                            intended department) without
                                            needing to open a separate
                                            surface. Fields come straight
                                            off `requested_production` on
                                            the persisted row. */}
                                        {rp ? (
                                            <div
                                                data-testid={`service-request-${r.id}-production`}
                                                className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3"
                                            >
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                                                    Requested Production
                                                </div>
                                                <div
                                                    data-testid={`service-request-${r.id}-production-name`}
                                                    className="text-sm font-semibold text-foreground mt-1"
                                                >
                                                    {rp.name}
                                                </div>
                                                <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
                                                    {ptype ? (
                                                        <span
                                                            data-testid={`service-request-${r.id}-production-type`}
                                                            className="inline-flex items-center gap-1.5"
                                                        >
                                                            <ptype.Icon size={12} />
                                                            {ptype.label}
                                                        </span>
                                                    ) : (
                                                        <span data-testid={`service-request-${r.id}-production-type`}>
                                                            {rp.production_type}
                                                        </span>
                                                    )}
                                                    {rp.destination_key ? (
                                                        <span
                                                            data-testid={`service-request-${r.id}-production-destination`}
                                                            className="inline-flex items-center gap-1.5"
                                                        >
                                                            {dest ? (
                                                                <>
                                                                    <dest.Icon size={12} />
                                                                    {dest.label}
                                                                </>
                                                            ) : (
                                                                rp.destination_key
                                                            )}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) : null}
                                        {r.reason ? (
                                            <p
                                                data-testid={`service-request-${r.id}-reason`}
                                                className="text-[12px] text-muted-foreground mt-2 whitespace-pre-wrap"
                                            >
                                                &ldquo;{r.reason}&rdquo;
                                            </p>
                                        ) : null}
                                        {r.notes ? (
                                            <p className="text-[11px] text-muted-foreground mt-2 italic">
                                                Operator note: {r.notes}
                                            </p>
                                        ) : null}
                                    </div>
                                    {r.status === "pending" ? (
                                        <div className="flex flex-col gap-2 items-stretch w-full sm:w-auto">
                                            <Label
                                                htmlFor={`service-notes-${r.id}`}
                                                className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground"
                                            >
                                                Optional note
                                            </Label>
                                            <Textarea
                                                id={`service-notes-${r.id}`}
                                                data-testid={`service-request-${r.id}-notes`}
                                                rows={2}
                                                className="w-full sm:w-64 bg-secondary border-transparent text-[12px]"
                                                value={decisionNotes[r.id] || ""}
                                                onChange={(e) =>
                                                    setDecisionNotes((prev) => ({
                                                        ...prev,
                                                        [r.id]: e.target.value,
                                                    }))
                                                }
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    type="button"
                                                    data-testid={`service-request-${r.id}-approve`}
                                                    disabled={busyId === r.id}
                                                    onClick={() => decide(r, "approve")}
                                                    className="flex-1 min-tap bg-emerald-500 hover:bg-emerald-400 text-neutral-950 font-semibold"
                                                >
                                                    <CheckCircle2 size={14} className="mr-1" />
                                                    Approve
                                                </Button>
                                                <Button
                                                    type="button"
                                                    data-testid={`service-request-${r.id}-deny`}
                                                    variant="outline"
                                                    disabled={busyId === r.id}
                                                    onClick={() => decide(r, "deny")}
                                                    className="flex-1 min-tap text-red-300 border-red-500/40 hover:bg-red-500/10"
                                                >
                                                    <XCircle size={14} className="mr-1" />
                                                    Deny
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-muted-foreground">
                                            Decided by {r.decided_by || "operator"}
                                            {r.decided_at ? ` · ${r.decided_at.slice(0, 16).replace("T", " ")}` : ""}
                                        </div>
                                    )}
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
